import {
  CHANGE_ACTIONS,
  CHANGE_CLASSES,
  CHANGE_LINKS,
  CHANGE_PARTIES,
  CHANGE_STATUSES,
  CHANGE_STEPS,
  CHANGE_TYPES,
  type ChangeAction,
  type ChangeClass,
  type ChangeDetail,
  type ChangeLink,
  type ChangeParty,
  type ChangeSettings,
  type ChangeStatus,
  type ChangeStep,
  type ChangeSummary,
  type ChangeTotals,
  type ChangeType,
  type ContractSummary,
  can,
  type PurchaseRequestSummary,
  type SetSummary,
  seesMoney,
} from '@epc/contracts';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate, useParams, useSearch } from '@tanstack/react-router';
import { AlertTriangle, Plus, Trash2 } from 'lucide-react';
import { useCallback, useState } from 'react';
import { useCrumbs } from '../../layout/crumbs';
import { api } from '../../lib/api';
import { date, dateTime, plural, tenge, tengeShort } from '../../lib/format';
import { projectQuery } from '../../lib/projects';
import { useMe } from '../../lib/session';
import { Button } from '../../ui/button';
import { Dialog } from '../../ui/dialog';
import { Badge, EmptyState, ErrorBox, Spinner, type Tone, toast } from '../../ui/feedback';
import { Checkbox, Field, Input, Select, Textarea } from '../../ui/form';
import { KeyValue, Page, PageHeader, Panel, TitleBlock } from '../../ui/layout';
import { ScrollX } from '../../ui/scroll';
import { estimatesQuery } from '../estimates/lib';
import { ProjectFilter, Tabs, useProjectDefault, useProjectsWith } from '../psd/shared';
import { ListSettingsLink } from '../reports/ListSettingsLink';

const STATUS_TONE: Record<ChangeStatus, Tone> = {
  draft: 'neutral',
  approval: 'info',
  approved: 'success',
  rejected: 'danger',
  cancelled: 'neutral',
  implemented: 'ink',
};

type Search = { project?: string; status?: string; tab?: 'list' | 'trend' };

const signed = (v: number | null | undefined) =>
  v === null || v === undefined ? '—' : `${v > 0 ? '+' : ''}${tengeShort(v)}`;
const days = (v: number | null | undefined) => (v ? `${v > 0 ? '+' : ''}${v} дн.` : '—');

function StatusBadge({ c }: { c: Pick<ChangeSummary, 'status' | 'minor' | 'atRisk'> }) {
  return (
    <span className="inline-flex flex-wrap gap-1">
      <Badge tone={STATUS_TONE[c.status]}>{CHANGE_STATUSES[c.status]}</Badge>
      {c.minor ? <Badge>мелкое, без маршрута</Badge> : null}
      {c.atRisk ? <Badge tone="danger">риск: работы до допсоглашения</Badge> : null}
    </span>
  );
}

/* ───────────────────────── Реестр ───────────────────────── */

export function ChangesPage() {
  const search = useSearch({ strict: false }) as Search;
  const navigate = useNavigate();
  const me = useMe();
  const setSearch = (patch: Partial<Search>) =>
    navigate({ to: '/changes', search: { ...search, ...patch } as never, replace: true });
  const requesters = useProjectsWith('changes.request');
  const [creating, setCreating] = useState(false);
  const [limits, setLimits] = useState(false);
  const tab = search.tab ?? 'list';
  useCrumbs(tab === 'trend' ? [{ label: 'Тренд и итог' }] : null);
  const staff = me.tenant?.staff ?? false;
  return (
    <Page>
      <PageHeader
        title="Изменения"
        subtitle="Запрос на изменение → анализ влияния → согласование → новые версии документов. Прежние версии остаются."
        actions={
          <>
            {staff && requesters.list.length ? (
              <Button
                variant="primary"
                size="lg"
                icon={<Plus className="size-4" />}
                onClick={() => setCreating(true)}
              >
                Новый запрос на изменение
              </Button>
            ) : null}
            {staff ? (
              <Button size="lg" variant="ghost" onClick={() => setLimits(true)}>
                Лимиты
              </Button>
            ) : null}
            {tab === 'list' ? <ListSettingsLink source="changes" /> : null}
          </>
        }
      />
      <Tabs
        value={tab}
        onChange={(v) => setSearch({ tab: v === 'list' ? undefined : v })}
        items={[
          { value: 'list', label: 'Реестр' },
          { value: 'trend', label: 'Тренд и итог' },
        ]}
      />
      <div className="mb-4 flex flex-col gap-2 md:flex-row">
        <ProjectFilter
          value={search.project}
          onChange={(v) => setSearch({ project: v })}
          perms={['changes.view']}
        />
        {tab === 'list' ? (
          <Select
            aria-label="Состояние"
            className="h-11 md:h-10 md:w-56"
            value={search.status ?? 'all'}
            onChange={(e) => setSearch({ status: e.target.value === 'all' ? undefined : e.target.value })}
          >
            <option value="all">Все</option>
            <option value="mine">Ждут моего решения</option>
            <option value="open">Черновики и на согласовании</option>
            <option value="approved">Утверждённые</option>
            <option value="closed">Закрытые</option>
          </Select>
        ) : null}
      </div>
      {tab === 'list' ? <ChangesList search={search} /> : <TotalsPanel projectId={search.project} />}
      {creating ? <NewChangeDialog projectId={search.project} onClose={() => setCreating(false)} /> : null}
      {limits ? <LimitsDialog onClose={() => setLimits(false)} /> : null}
    </Page>
  );
}

function ChangesList({ search }: { search: Search }) {
  const q = useQuery({
    queryKey: ['changes', search.project ?? 'all', search.status ?? 'all'],
    queryFn: () =>
      api<ChangeSummary[]>('/changes', { query: { projectId: search.project, status: search.status } }),
  });
  if (q.isPending) return <Spinner />;
  if (q.isError) return <ErrorBox error={q.error} />;
  if (!q.data.length)
    return (
      <EmptyState title="Запросов на изменение нет">
        Запрос создаётся вручную или сам — когда ПТО принимает ответ проектировщика, меняющий проект.
      </EmptyState>
    );
  return (
    <ul className="flex flex-col gap-2">
      {q.data.map((c) => (
        <li key={c.id}>
          <Link
            to="/changes/$changeId"
            params={{ changeId: c.id }}
            className="flex flex-col gap-1.5 rounded-lg border border-line bg-surface p-4 hover:border-ink-3 md:flex-row md:items-start md:gap-4"
          >
            <span className="num shrink-0 font-medium text-ink md:w-24">{c.number}</span>
            <span className="flex min-w-0 flex-1 flex-col gap-0.5">
              <span className="[overflow-wrap:anywhere] text-ink">{c.title}</span>
              <span className="text-[13px] text-ink-2">
                {CHANGE_TYPES[c.type]} · {CHANGE_CLASSES[c.changeClass]} · инициатор:{' '}
                {CHANGE_PARTIES[c.initiator]} · за счёт: {CHANGE_PARTIES[c.payer]} · {c.projectCode}
              </span>
              {c.waiting.length ? (
                <span className="text-[13px] text-ink-2">ждёт: {c.waiting.join(', ')}</span>
              ) : null}
            </span>
            <span className="flex flex-wrap items-center gap-2 md:flex-col md:items-end">
              <StatusBadge c={c} />
              <span className="num text-[13px] text-ink-2">
                {c.revenueDelta !== null ? `цена ${signed(c.revenueDelta)} · ` : ''}срок {days(c.daysDelta)}
              </span>
            </span>
          </Link>
        </li>
      ))}
    </ul>
  );
}

function TotalsPanel({ projectId }: { projectId?: string }) {
  const q = useQuery({
    queryKey: ['change-totals', projectId ?? 'all'],
    queryFn: () => api<ChangeTotals>('/changes/totals', { query: { projectId } }),
  });
  if (q.isPending) return <Spinner />;
  if (q.isError) return <ErrorBox error={q.error} />;
  const t = q.data;
  const h = t.moneyHidden;
  return (
    <div className="flex flex-col gap-4">
      <TitleBlock
        items={[
          {
            label: 'Утверждено',
            value: h ? t.approved.count : signed(t.approved.revenue),
            note: `${plural(t.approved.count, 'запрос', 'запроса', 'запросов')} · срок ${days(t.approved.days)}${t.approved.cost !== null ? ` · себестоимость ${signed(t.approved.cost)}` : ''}`,
          },
          {
            label: 'Не утверждено (в прогнозе отдельно)',
            value: h ? t.pending.count : signed(t.pending.revenue),
            note: `${plural(t.pending.count, 'запрос', 'запроса', 'запросов')} · срок ${days(t.pending.days)}; бюджет не меняют`,
          },
          {
            label: 'Риск: работы до допсоглашения',
            value: h ? t.atRisk.count : signed(t.atRisk.revenue),
            tone: t.atRisk.count ? 'danger' : 'default',
            note: `${plural(t.atRisk.count, 'запрос', 'запроса', 'запросов')} за счёт заказчика`,
          },
        ]}
      />
      {h ? <p className="text-[13px] text-ink-2">Суммы скрыты: нужен уровень денег «Все деньги».</p> : null}
      <Panel
        title="Тренд утверждённых изменений"
        meta="накопленным итогом от базового варианта"
        bodyClassName="p-0"
      >
        {t.trend.length ? (
          <ScrollX>
            <table className="w-full min-w-[480px] text-[14px]">
              <thead>
                <tr className="bg-surface-2 text-left text-[12px] text-ink-2">
                  <th className="py-2.5 pr-3 pl-5 font-medium">Дата</th>
                  <th className="py-2.5 pr-3 text-right font-medium">Цена договора</th>
                  <th className="py-2.5 pr-3 text-right font-medium">Себестоимость</th>
                  <th className="py-2.5 pr-5 text-right font-medium">Срок</th>
                </tr>
              </thead>
              <tbody>
                {t.trend.map((r) => (
                  <tr key={r.date} className="border-t border-line">
                    <td className="py-2.5 pr-3 pl-5">{date(r.date)}</td>
                    <td className="num py-2.5 pr-3 text-right">{signed(r.revenue)}</td>
                    <td className="num py-2.5 pr-3 text-right">{signed(r.cost)}</td>
                    <td className="num py-2.5 pr-5 text-right">{days(r.days)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </ScrollX>
        ) : (
          <p className="px-5 py-4 text-[14px] text-ink-2">Утверждённых изменений пока нет.</p>
        )}
      </Panel>
      <div className="grid gap-4 lg:grid-cols-2">
        {(
          [
            ['По видам', t.byType],
            ['По инициаторам', t.byInitiator],
          ] as const
        ).map(([title, list]) => (
          <Panel key={title} title={title} bodyClassName="p-0">
            <ul className="divide-y divide-line">
              {list.map((r) => (
                <li
                  key={r.key}
                  className="flex flex-wrap items-center justify-between gap-2 px-5 py-2.5 text-[14px]"
                >
                  <span>{r.key}</span>
                  <span className="num text-ink-2">
                    {r.count} · {h ? '' : `${signed(r.revenue)} · `}
                    {days(r.days)}
                  </span>
                </li>
              ))}
              {!list.length ? <li className="px-5 py-3 text-[14px] text-ink-2">Нет данных</li> : null}
            </ul>
          </Panel>
        ))}
      </div>
    </div>
  );
}

function LimitsDialog({ onClose }: { onClose: () => void }) {
  const me = useMe();
  const qc = useQueryClient();
  const admin = can(me.access, 'admin.settings');
  const q = useQuery({
    queryKey: ['change-settings'],
    queryFn: () => api<ChangeSettings>('/changes/settings'),
  });
  const [f, setF] = useState<{ minorPercent: string; directorLimit: string } | null>(null);
  const v =
    f ??
    (q.data
      ? { minorPercent: String(q.data.minorPercent), directorLimit: String(q.data.directorLimit) }
      : null);
  const m = useMutation({
    mutationFn: () =>
      api<ChangeSettings>('/changes/settings', {
        method: 'PUT',
        body: {
          minorPercent: Number(v!.minorPercent.replace(',', '.')),
          directorLimit: Number(v!.directorLimit.replace(/\s/g, '')),
        },
      }),
    onSuccess: (d) => {
      qc.setQueryData(['change-settings'], d);
      toast('Лимиты сохранены');
      onClose();
    },
  });
  return (
    <Dialog
      open
      onOpenChange={(o) => !o && onClose()}
      title="Лимиты изменений"
      description="Мелкое изменение в пределах процента стоимости позиции проходит без маршрута с записью в истории; выше суммы — в маршрут добавляется Директор."
      footer={
        admin && seesMoney(me.access) ? (
          <Button variant="primary" className="h-11 md:h-10" loading={m.isPending} onClick={() => m.mutate()}>
            Сохранить
          </Button>
        ) : null
      }
    >
      {!v ? (
        <Spinner />
      ) : (
        <div className="grid gap-4">
          <Field label="Мелкое изменение, % стоимости позиции">
            <Input
              className="h-11 md:h-10"
              disabled={!admin}
              value={v.minorPercent}
              onChange={(e) => setF({ ...v, minorPercent: e.target.value })}
            />
          </Field>
          <Field
            label="Директор согласует свыше, ₸"
            hint={seesMoney(me.access) ? tenge(Number(v.directorLimit) || 0) : undefined}
          >
            {seesMoney(me.access) ? (
              <Input
                className="h-11 md:h-10"
                disabled={!admin}
                inputMode="numeric"
                value={v.directorLimit}
                onChange={(e) => setF({ ...v, directorLimit: e.target.value })}
              />
            ) : (
              <p className="text-[14px] text-ink-2">сумма скрыта</p>
            )}
          </Field>
          {!admin ? <p className="text-[13px] text-ink-2">Лимиты меняет администратор.</p> : null}
          <ErrorBox error={m.error} />
        </div>
      )}
    </Dialog>
  );
}

/* ───────────────────────── Новый запрос ───────────────────────── */

interface ItemDraft {
  linkType: ChangeLink;
  linkId: string;
  description: string;
  /** Название уже связанной записи (при правке) */
  title?: string;
}

function useLinkOptions(projectId: string) {
  const enabled = Boolean(projectId);
  const sets = useQuery({
    queryKey: ['psd-sets', projectId],
    queryFn: () => api<SetSummary[]>('/psd/sets', { query: { projectId } }),
    enabled,
    retry: false,
  });
  const contracts = useQuery({
    queryKey: ['contracts', projectId],
    queryFn: () => api<ContractSummary[]>('/contracts', { query: { projectId } }),
    enabled,
    retry: false,
  });
  const requests = useQuery({
    queryKey: ['procurement-requests-change', projectId],
    queryFn: () =>
      api<PurchaseRequestSummary[]>('/procurement/requests', { query: { projectId, scope: 'all' } }),
    enabled,
    retry: false,
  });
  const estimates = useQuery({ ...estimatesQuery({ projectId }), retry: false });
  const project = useQuery({ ...projectQuery(projectId), enabled });
  const opts: Record<ChangeLink, Array<{ id: string; label: string }>> = {
    set: (sets.data ?? []).map((s) => ({ id: s.id, label: s.code })),
    sheet: [],
    contract: (contracts.data ?? []).map((c) => ({ id: c.id, label: `${c.number} ${c.counterpartyName}` })),
    request: (requests.data ?? []).map((r) => ({ id: r.id, label: `${r.number} ${r.title}` })),
    milestone: (project.data?.milestones ?? []).map((m) => ({
      id: m.id,
      label: `${m.name} (${date(m.baselineDate)})`,
    })),
    activity: [],
    budget_line: [],
    estimate: (estimates.data ?? []).map((e) => ({ id: e.id, label: `${e.code} ${e.name}` })),
    other: [],
  };
  return opts;
}

export function NewChangeDialog({
  projectId,
  preset,
  edit,
  onClose,
}: {
  projectId?: string;
  /** Правка черновика */
  edit?: ChangeDetail;
  preset?: Partial<{
    title: string;
    reason: string;
    basis: string;
    remarkId: string;
    questionId: string;
    type: ChangeType;
    /** Затронутые записи сразу: договор или заявка, из карточки которых открыт запрос (ДГ-08, ИЗ-16) */
    links: Array<{ linkType: ChangeLink; linkId: string; title: string }>;
  }>;
  onClose: () => void;
}) {
  const me = useMe();
  const qc = useQueryClient();
  const navigate = useNavigate();
  const { list } = useProjectsWith('changes.request');
  const money = seesMoney(me.access);
  const str = (v: number | null | undefined) => (v === null || v === undefined ? '' : String(v));
  const [f, setF] = useState({
    projectId:
      edit?.projectId ??
      (projectId && list.some((p) => p.id === projectId) ? projectId : (list[0]?.id ?? '')),
    title: edit?.title ?? preset?.title ?? '',
    type: (edit?.type ?? preset?.type ?? 'psd') as ChangeType,
    changeClass: (edit?.changeClass ?? 'within_contract') as ChangeClass,
    initiator: (edit?.initiator ?? 'us') as ChangeParty,
    payer: (edit?.payer ?? 'us') as ChangeParty,
    reason: edit?.reason ?? preset?.reason ?? '',
    basis: edit?.basis ?? preset?.basis ?? '',
    urgency: (edit?.urgency ?? 'normal') as 'normal' | 'urgent',
    revenueDelta: str(edit?.revenueDelta),
    costDelta: str(edit?.costDelta),
    baseAmount: str(edit?.baseAmount),
    daysDelta: str(edit?.daysDelta),
    affectsFinPlan: edit?.affectsFinPlan ?? false,
    needsDesigner: edit?.needsDesigner ?? false,
    workStarted: edit?.workStarted ?? false,
  });
  const setProjectDefaultNewChangeDialog = useCallback(
    (id: string) => setF((x) => ({ ...x, projectId: id })),
    [],
  );
  useProjectDefault(edit ? [] : list, projectId, f.projectId, setProjectDefaultNewChangeDialog);
  const [items, setItems] = useState<ItemDraft[]>(
    edit
      ? edit.items.map((i) => ({
          linkType: i.linkType,
          linkId: i.linkId ?? '',
          description: i.description ?? '',
          title: i.title,
        }))
      : (preset?.links ?? []).map((l) => ({ ...l, description: '' })),
  );
  const opts = useLinkOptions(f.projectId);
  const set = <K extends keyof typeof f>(k: K, v: (typeof f)[K]) => setF((x) => ({ ...x, [k]: v }));
  const num = (s: string) => (s.trim() ? Number(s.replace(/\s/g, '').replace(',', '.')) : null);
  const m = useMutation({
    mutationFn: () =>
      api<ChangeDetail>(edit ? `/changes/${edit.id}` : '/changes', {
        method: edit ? 'PATCH' : 'POST',
        body: {
          ...(edit
            ? {}
            : {
                projectId: f.projectId,
                remarkId: preset?.remarkId ?? null,
                questionId: preset?.questionId ?? null,
              }),
          title: f.title.trim(),
          type: f.type,
          changeClass: f.changeClass,
          initiator: f.initiator,
          payer: f.payer,
          reason: f.reason.trim(),
          basis: f.basis.trim() || null,
          urgency: f.urgency,
          // Без доступа к деньгам суммы не отправляются вовсе: их не стереть и не поменять
          ...(money
            ? {
                revenueDelta: num(f.revenueDelta),
                costDelta: num(f.costDelta),
                baseAmount: num(f.baseAmount),
              }
            : {}),
          daysDelta: num(f.daysDelta),
          affectsFinPlan: f.affectsFinPlan,
          needsDesigner: f.needsDesigner,
          workStarted: f.workStarted,
          items: items
            .filter((i) => i.linkType === 'other' || i.linkId)
            .map((i) => ({
              linkType: i.linkType,
              linkId: i.linkId || null,
              description: i.description.trim() || null,
            })),
        },
      }),
    onSuccess: (c) => {
      qc.invalidateQueries({ queryKey: ['changes'] });
      if (edit) {
        qc.setQueryData(['change', c.id], c);
        toast(`${c.number} сохранён`);
        onClose();
        return;
      }
      toast(`Создан ${c.number} — черновик. Проверьте влияние и отправьте на согласование`);
      onClose();
      navigate({ to: '/changes/$changeId', params: { changeId: c.id } });
    },
  });
  return (
    <Dialog
      open
      width="lg"
      onOpenChange={(o) => !o && onClose()}
      title={edit ? `Правка ${edit.number}` : 'Новый запрос на изменение'}
      description="Маршрут согласования соберётся по типу, классу и сумме при отправке."
      footer={
        <>
          <Button variant="ghost" className="h-11 md:h-10" onClick={onClose}>
            Отмена
          </Button>
          <Button
            variant="primary"
            className="h-11 md:h-10"
            loading={m.isPending}
            disabled={!f.projectId || f.title.trim().length < 3 || f.reason.trim().length < 3}
            onClick={() => m.mutate()}
          >
            {edit ? 'Сохранить' : 'Создать черновик'}
          </Button>
        </>
      }
    >
      <div className="grid gap-4 sm:grid-cols-2">
        <Field label="Проект" className="sm:col-span-2">
          <Select
            className="h-11 md:h-10"
            disabled={Boolean(edit)}
            value={f.projectId}
            onChange={(e) => {
              set('projectId', e.target.value);
              setItems([]);
            }}
          >
            {list.map((p) => (
              <option key={p.id} value={p.id}>
                {p.code} {p.name}
              </option>
            ))}
          </Select>
        </Field>
        <Field label="Что меняется" className="sm:col-span-2">
          <Input className="h-11 md:h-10" value={f.title} onChange={(e) => set('title', e.target.value)} />
        </Field>
        <Field label="Вид">
          <Select
            className="h-11 md:h-10"
            value={f.type}
            onChange={(e) => set('type', e.target.value as ChangeType)}
          >
            {(Object.keys(CHANGE_TYPES) as ChangeType[]).map((k) => (
              <option key={k} value={k}>
                {CHANGE_TYPES[k]}
              </option>
            ))}
          </Select>
        </Field>
        <Field label="Класс">
          <Select
            className="h-11 md:h-10"
            value={f.changeClass}
            onChange={(e) => set('changeClass', e.target.value as ChangeClass)}
          >
            {(Object.keys(CHANGE_CLASSES) as ChangeClass[]).map((k) => (
              <option key={k} value={k}>
                {CHANGE_CLASSES[k]}
              </option>
            ))}
          </Select>
        </Field>
        <Field label="Инициатор">
          <Select
            className="h-11 md:h-10"
            value={f.initiator}
            onChange={(e) => set('initiator', e.target.value as ChangeParty)}
          >
            {(Object.keys(CHANGE_PARTIES) as ChangeParty[]).map((k) => (
              <option key={k} value={k}>
                {CHANGE_PARTIES[k]}
              </option>
            ))}
          </Select>
        </Field>
        <Field label="За чей счёт">
          <Select
            className="h-11 md:h-10"
            value={f.payer}
            onChange={(e) => set('payer', e.target.value as ChangeParty)}
          >
            {(Object.keys(CHANGE_PARTIES) as ChangeParty[]).map((k) => (
              <option key={k} value={k}>
                {CHANGE_PARTIES[k]}
              </option>
            ))}
          </Select>
        </Field>
        <Field label="Причина" className="sm:col-span-2">
          <Textarea rows={3} value={f.reason} onChange={(e) => set('reason', e.target.value)} />
        </Field>
        <Field
          label="Документ-основание"
          hint="Письмо, ответ на вопрос, замечание экспертизы"
          className="sm:col-span-2"
        >
          <Input className="h-11 md:h-10" value={f.basis} onChange={(e) => set('basis', e.target.value)} />
        </Field>
        {money ? (
          <>
            <Field label="Цена для заказчика, ₸" hint="Плюс — дороже, минус — дешевле">
              <Input
                className="h-11 md:h-10"
                inputMode="decimal"
                value={f.revenueDelta}
                onChange={(e) => set('revenueDelta', e.target.value)}
              />
            </Field>
            <Field label="Себестоимость, ₸">
              <Input
                className="h-11 md:h-10"
                inputMode="decimal"
                value={f.costDelta}
                onChange={(e) => set('costDelta', e.target.value)}
              />
            </Field>
            <Field label="Стоимость позиции, ₸" hint="Для лимита мелких изменений">
              <Input
                className="h-11 md:h-10"
                inputMode="decimal"
                value={f.baseAmount}
                onChange={(e) => set('baseAmount', e.target.value)}
              />
            </Field>
          </>
        ) : (
          <p className="text-[13px] text-ink-2 sm:col-span-2">
            Суммы внесёт сотрудник с уровнем денег «Все деньги».
          </p>
        )}
        <Field label="Сдвиг срока, дней">
          <Input
            className="h-11 md:h-10"
            inputMode="numeric"
            value={f.daysDelta}
            onChange={(e) => set('daysDelta', e.target.value)}
          />
        </Field>
        <Field label="Срочность">
          <Select
            className="h-11 md:h-10"
            value={f.urgency}
            onChange={(e) => set('urgency', e.target.value as 'normal' | 'urgent')}
          >
            <option value="normal">Обычная</option>
            <option value="urgent">Срочная</option>
          </Select>
        </Field>
        <div className="flex flex-col gap-2 sm:col-span-2">
          <Checkbox
            checked={f.affectsFinPlan}
            onChange={(e) => set('affectsFinPlan', e.target.checked)}
            label="Меняются ФП или ДДС"
            description="В маршрут — финансовый менеджер и куратор; финансовый менеджер узнает сразу"
          />
          <Checkbox
            checked={f.needsDesigner}
            onChange={(e) => set('needsDesigner', e.target.checked)}
            label="Нужно согласование проектировщика"
          />
          <Checkbox
            checked={f.workStarted}
            onChange={(e) => set('workStarted', e.target.checked)}
            label="Работы уже идут"
            description="За счёт заказчика без подписанного допсоглашения — риск в марже и деньгах"
          />
        </div>
        <div className="sm:col-span-2">
          <div className="mb-1.5 flex items-center justify-between">
            <span className="text-[13px] font-medium text-ink">Что затрагивает</span>
            <Button
              variant="ghost"
              className="h-11 md:h-9"
              icon={<Plus className="size-4" />}
              onClick={() => setItems((x) => [...x, { linkType: 'set', linkId: '', description: '' }])}
            >
              Добавить
            </Button>
          </div>
          <ul className="flex flex-col gap-2">
            {items.map((it, i) => (
              <li
                // biome-ignore lint/suspicious/noArrayIndexKey: строки черновика без постоянного ключа
                key={i}
                className="grid gap-2 rounded-md border border-line p-2 sm:grid-cols-[160px_1fr_auto]"
              >
                <Select
                  aria-label="Что"
                  className="h-11 md:h-10"
                  value={it.linkType}
                  onChange={(e) =>
                    setItems((x) =>
                      x.map((y, j) =>
                        j === i ? { ...y, linkType: e.target.value as ChangeLink, linkId: '' } : y,
                      ),
                    )
                  }
                >
                  {(Object.keys(CHANGE_LINKS) as ChangeLink[])
                    .filter(
                      (k) =>
                        ['set', 'contract', 'request', 'milestone', 'estimate', 'other'].includes(k) ||
                        k === it.linkType,
                    )
                    .map((k) => (
                      <option key={k} value={k}>
                        {CHANGE_LINKS[k]}
                      </option>
                    ))}
                </Select>
                {it.linkType === 'other' ? (
                  <Input
                    aria-label="Описание"
                    className="h-11 md:h-10"
                    value={it.description}
                    onChange={(e) =>
                      setItems((x) => x.map((y, j) => (j === i ? { ...y, description: e.target.value } : y)))
                    }
                  />
                ) : (
                  <Select
                    aria-label="Запись"
                    className="h-11 md:h-10"
                    value={it.linkId}
                    onChange={(e) =>
                      setItems((x) => x.map((y, j) => (j === i ? { ...y, linkId: e.target.value } : y)))
                    }
                  >
                    <option value="">— выберите —</option>
                    {it.linkId && it.title && !opts[it.linkType].some((o) => o.id === it.linkId) ? (
                      <option value={it.linkId}>{it.title}</option>
                    ) : null}
                    {opts[it.linkType].map((o) => (
                      <option key={o.id} value={o.id}>
                        {o.label}
                      </option>
                    ))}
                  </Select>
                )}
                <Button
                  variant="ghost"
                  className="h-11 md:h-10"
                  aria-label="Убрать"
                  icon={<Trash2 className="size-4" />}
                  onClick={() => setItems((x) => x.filter((_, j) => j !== i))}
                />
              </li>
            ))}
          </ul>
        </div>
      </div>
      <ErrorBox error={m.error} className="mt-4" />
    </Dialog>
  );
}

/* ───────────────────────── Карточка ───────────────────────── */

export function ChangePage() {
  const { changeId } = useParams({ strict: false }) as { changeId: string };
  const qc = useQueryClient();
  const q = useQuery({
    queryKey: ['change', changeId],
    queryFn: () => api<ChangeDetail>(`/changes/${changeId}`),
  });
  const [act, setAct] = useState<{ action: ChangeAction; step?: ChangeStep } | null>(null);
  const [editing, setEditing] = useState(false);
  useCrumbs(
    q.data
      ? [{ label: q.data.projectCode, to: `/changes?project=${q.data.projectId}` }, { label: q.data.number }]
      : null,
    q.error,
  );
  if (q.isPending)
    return (
      <Page>
        <Spinner />
      </Page>
    );
  if (q.isError)
    return (
      <Page>
        <ErrorBox error={q.error} />
      </Page>
    );
  const c = q.data;
  const refresh = (d: ChangeDetail) => {
    qc.setQueryData(['change', changeId], d);
    qc.invalidateQueries({ queryKey: ['changes'] });
    qc.invalidateQueries({ queryKey: ['change-totals'] });
  };
  const main = c.actions.filter((a) => !['approve', 'reject', 'comment'].includes(a));
  const canEdit = c.status === 'draft' && c.actions.includes('submit');
  const mine = c.approvals.filter((a) => a.mine);
  return (
    <Page>
      <PageHeader
        title={`Запрос ${c.number}`}
        subtitle={<span className="[overflow-wrap:anywhere]">{c.title}</span>}
        actions={
          <>
            {mine.map((a) => (
              <Button
                key={a.step}
                variant="primary"
                size="lg"
                onClick={() => setAct({ action: 'approve', step: a.step })}
              >
                Согласовать: {CHANGE_STEPS[a.step]}
              </Button>
            ))}
            {mine.length ? (
              <Button
                size="lg"
                variant="danger"
                onClick={() => setAct({ action: 'reject', step: mine[0]!.step })}
              >
                Отклонить
              </Button>
            ) : null}
            {main.map((a, i) => (
              <Button
                key={a}
                size="lg"
                variant={!mine.length && i === 0 ? 'primary' : a === 'cancel' ? 'danger' : 'secondary'}
                onClick={() => setAct({ action: a })}
              >
                {CHANGE_ACTIONS[a]}
              </Button>
            ))}
            {canEdit ? (
              <Button size="lg" variant="secondary" onClick={() => setEditing(true)}>
                Изменить
              </Button>
            ) : null}
            {c.type === 'psd' || c.items.some((x) => x.linkType === 'sheet' || x.linkType === 'set') ? (
              // Извещение об изменении ПСД (АВ-03) — документ PDF по запросу
              <a
                href={`/api/v1/changes/${c.id}/notice.pdf`}
                target="_blank"
                rel="noreferrer"
                className="inline-flex h-11 items-center gap-2 rounded-md border border-line px-4 text-[14px] font-medium hover:bg-surface-2"
              >
                Извещение об изменении (PDF)
              </a>
            ) : null}
            {c.actions.includes('comment') ? (
              <Button size="lg" variant="ghost" onClick={() => setAct({ action: 'comment' })}>
                Комментарий
              </Button>
            ) : null}
          </>
        }
      >
        <div className="mt-2">
          <StatusBadge c={c} />
        </div>
      </PageHeader>
      {c.atRisk ? (
        <div
          className="mb-4 flex items-start gap-2 rounded-lg border border-danger/40 bg-danger-soft p-4 text-[14px] text-danger"
          role="alert"
        >
          <AlertTriangle className="mt-0.5 size-4 shrink-0" />
          Изменение за счёт заказчика, работы уже идут, а допсоглашение не подписано — сумма показывается как
          риск в марже и деньгах.
        </div>
      ) : null}
      <div className="grid gap-4 lg:grid-cols-[1fr_380px]">
        <div className="flex min-w-0 flex-col gap-4">
          <Panel title="Суть">
            <KeyValue
              items={[
                { label: 'Вид', value: CHANGE_TYPES[c.type] },
                { label: 'Класс', value: CHANGE_CLASSES[c.changeClass] },
                { label: 'Инициатор', value: CHANGE_PARTIES[c.initiator] },
                { label: 'За чей счёт', value: CHANGE_PARTIES[c.payer] },
                { label: 'Причина', value: <span className="whitespace-pre-wrap">{c.reason}</span> },
                { label: 'Основание', value: c.basis },
                {
                  label: 'Источник',
                  value: c.question ? (
                    <Link
                      to="/supervision/questions/$questionId"
                      params={{ questionId: c.question.id }}
                      className="inline-flex min-h-11 items-center underline-offset-4 hover:underline md:min-h-0"
                    >
                      Вопрос {c.question.number}
                    </Link>
                  ) : c.remark ? (
                    <Link
                      to="/supervision/remarks/$remarkId"
                      params={{ remarkId: c.remark.id }}
                      className="inline-flex min-h-11 items-center underline-offset-4 hover:underline md:min-h-0"
                    >
                      Замечание {c.remark.number}
                    </Link>
                  ) : c.extraWorkAct ? (
                    <Link
                      to="/site/extra-works/$actId"
                      params={{ actId: c.extraWorkAct.id }}
                      className="inline-flex min-h-11 items-center underline-offset-4 hover:underline md:min-h-0"
                    >
                      Акт {c.extraWorkAct.number}
                    </Link>
                  ) : null,
                },
                {
                  label: 'Цена для заказчика',
                  value: c.moneyHidden ? 'сумма скрыта' : signed(c.revenueDelta),
                },
                { label: 'Себестоимость', value: c.moneyHidden ? 'сумма скрыта' : signed(c.costDelta) },
                { label: 'Срок', value: days(c.daysDelta) },
                { label: 'ФП и ДДС', value: c.affectsFinPlan ? 'меняются' : 'не меняются' },
                {
                  label: 'Допсоглашение подписано',
                  value: c.amendmentSignedAt ? date(c.amendmentSignedAt) : null,
                },
                { label: 'Автор', value: `${c.createdBy ?? '—'}, ${dateTime(c.createdAt)}` },
              ]}
            />
          </Panel>
          <Panel title="Анализ влияния" meta="затронутые записи и их состояние" bodyClassName="p-0">
            {c.items.length ? (
              <ul className="divide-y divide-line">
                {c.items.map((it) => (
                  <li key={it.id} className="flex flex-col gap-1 px-5 py-3">
                    {it.path ? (
                      <Link
                        to={it.path}
                        className="inline-flex min-h-11 items-center font-medium text-ink underline-offset-4 hover:underline md:min-h-0"
                      >
                        {it.title}
                      </Link>
                    ) : (
                      <span className="font-medium text-ink">{it.title}</span>
                    )}
                    {it.facts.length ? (
                      <span className="text-[13px] text-ink-2">{it.facts.join(' · ')}</span>
                    ) : null}
                    {it.description ? <span className="text-[13px]">{it.description}</span> : null}
                  </li>
                ))}
              </ul>
            ) : (
              <p className="px-5 py-4 text-[14px] text-ink-2">Затронутые записи не указаны.</p>
            )}
          </Panel>
        </div>
        <div className="flex flex-col gap-4">
          <Panel title="Маршрут согласования" meta={c.round > 1 ? `раунд ${c.round}` : undefined}>
            {c.approvals.length ? (
              <ol className="flex flex-col gap-2 text-[14px]">
                {c.approvals.map((a) => (
                  <li key={a.step} className="flex flex-col gap-0.5 border-l-2 border-line pl-3">
                    <span className="flex flex-wrap items-center gap-2">
                      {CHANGE_STEPS[a.step]}
                      <Badge
                        tone={
                          a.status === 'approved' ? 'success' : a.status === 'rejected' ? 'danger' : 'neutral'
                        }
                      >
                        {a.status === 'approved'
                          ? 'согласовано'
                          : a.status === 'rejected'
                            ? 'отклонено'
                            : 'ждёт'}
                      </Badge>
                    </span>
                    {a.decidedBy ? (
                      <span className="text-[12px] text-ink-2">
                        {a.decidedBy}, {dateTime(a.decidedAt)}
                      </span>
                    ) : null}
                    {a.comment ? <span className="whitespace-pre-wrap text-[13px]">{a.comment}</span> : null}
                  </li>
                ))}
              </ol>
            ) : (
              <p className="text-[14px] text-ink-2">
                {c.minor
                  ? 'Мелкое изменение — утверждено без маршрута.'
                  : 'Маршрут соберётся при отправке на согласование.'}
              </p>
            )}
          </Panel>
          <Panel title="Ход">
            <ol className="flex flex-col gap-3 text-[14px]">
              {c.events.map((e, i) => (
                // biome-ignore lint/suspicious/noArrayIndexKey: события идут по времени и не меняются
                <li key={i} className="border-l-2 border-line pl-3">
                  <div>{e.action}</div>
                  <div className="text-[12px] text-ink-2">
                    {dateTime(e.at)}
                    {e.by ? ` · ${e.by}` : ''}
                  </div>
                  {e.comment ? <div className="mt-1 whitespace-pre-wrap">{e.comment}</div> : null}
                </li>
              ))}
            </ol>
          </Panel>
        </div>
      </div>
      {editing ? <NewChangeDialog edit={c} onClose={() => setEditing(false)} /> : null}
      {act ? (
        <ChangeActionDialog
          change={c}
          action={act.action}
          step={act.step}
          onClose={() => setAct(null)}
          onDone={refresh}
        />
      ) : null}
    </Page>
  );
}

function ChangeActionDialog({
  change,
  action,
  step,
  onClose,
  onDone,
}: {
  change: ChangeDetail;
  action: ChangeAction;
  step?: ChangeStep;
  onClose: () => void;
  onDone: (d: ChangeDetail) => void;
}) {
  const [comment, setComment] = useState('');
  const needComment = ['reject', 'cancel', 'comment'].includes(action);
  const m = useMutation({
    mutationFn: () =>
      api<ChangeDetail>(`/changes/${change.id}/action`, {
        body: { action, step, comment: comment.trim() || null },
      }),
    onSuccess: (d) => {
      onDone(d);
      toast(
        d.status === 'approved' && action !== 'comment'
          ? `${d.number} утверждён: ответственным поставлены поручения на новые версии`
          : `${d.number}: готово`,
      );
      onClose();
    },
  });
  return (
    <Dialog
      open
      onOpenChange={(o) => !o && onClose()}
      title={`${CHANGE_ACTIONS[action]}${step ? `: ${CHANGE_STEPS[step]}` : ''}`}
      description={
        action === 'submit'
          ? 'Мелкое изменение в пределах лимита утвердится сразу; иначе маршрут соберётся по типу, классу и сумме.'
          : undefined
      }
      footer={
        <>
          <Button variant="ghost" className="h-11 md:h-10" onClick={onClose}>
            Отмена
          </Button>
          <Button
            variant={action === 'reject' || action === 'cancel' ? 'danger' : 'primary'}
            className="h-11 md:h-10"
            loading={m.isPending}
            disabled={needComment && !comment.trim()}
            onClick={() => m.mutate()}
          >
            {CHANGE_ACTIONS[action]}
          </Button>
        </>
      }
    >
      <Field label={needComment ? 'Комментарий (обязательно)' : 'Комментарий'}>
        <Textarea rows={3} value={comment} onChange={(e) => setComment(e.target.value)} />
      </Field>
      <ErrorBox error={m.error} className="mt-3" />
    </Dialog>
  );
}

/** Изменения, связанные с записью: видно с обеих сторон связи */
export function LinkedChanges({ type, id }: { type: ChangeLink; id: string }) {
  const q = useQuery({
    queryKey: ['changes-linked', type, id],
    queryFn: () => api<ChangeSummary[]>(`/changes/linked/${type}/${id}`),
    retry: false,
  });
  if (!q.data?.length) return null;
  return (
    <Panel title="Изменения">
      <ul className="flex flex-col gap-1 text-[14px]">
        {q.data.map((c) => (
          <li key={c.id} className="flex flex-wrap items-center gap-2">
            <Link
              to="/changes/$changeId"
              params={{ changeId: c.id }}
              className="inline-flex min-h-11 items-center underline-offset-4 hover:underline md:min-h-0"
            >
              {c.number}
            </Link>
            <span className="text-ink-2">{c.title}</span>
            <Badge tone={STATUS_TONE[c.status]}>{CHANGE_STATUSES[c.status]}</Badge>
          </li>
        ))}
      </ul>
    </Panel>
  );
}
